import classNames from 'classnames';
import bindAll from 'lodash.bindall';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import LibraryItem from '../../containers/library-item.jsx';
import Modal from '../../containers/modal.jsx';
import Divider from '../divider/divider.jsx';
import Filter from '../filter/filter.jsx';
import TagButton from '../../containers/tag-button.jsx';
import Spinner from '../spinner/spinner.jsx';
import {CATEGORIES} from '../../../src/lib/libraries/decks/index.jsx';

import styles from './library.css';

const messages = defineMessages({
    filterPlaceholder: {
        id: 'gui.library.filterPlaceholder',
        defaultMessage: 'Search',
        description: 'Placeholder text for library search field'
    },
    allTag: {
        id: 'gui.library.allTag',
        defaultMessage: 'All',
        description: 'Label for library tag to revert to all items after filtering by tag.'
    },
    // Strings here need to be defined statically
    // https://formatjs.io/docs/getting-started/message-declaration/#pre-declaring-using-definemessage-for-later-consumption-less-recommended
    [CATEGORIES.gettingStarted]: {
        id: `gui.library.gettingStarted`,
        defaultMessage: 'Getting Started',
        description: 'Label for getting started category'
    },
    [CATEGORIES.basics]: {
        id: `gui.library.basics`,
        defaultMessage: 'Basics',
        description: 'Label for basics category'
    },
    [CATEGORIES.intermediate]: {
        id: `gui.library.intermediate`,
        defaultMessage: 'Intermediate',
        description: 'Label for intermediate category'
    },
    [CATEGORIES.prompts]: {
        id: `gui.library.prompts`,
        defaultMessage: 'Prompts',
        description: 'Label for prompts category'
    }
});

const ALL_TAG = {tag: 'all', intlLabel: messages.allTag};
const tagListPrefix = [ALL_TAG];
const SOURCE_SCOPE_PUBLIC = 'public';
const SOURCE_SCOPE_MINE = 'mine';

class LibraryComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClose',
            'handleFilterChange',
            'handleFilterClear',
            'handleMouseEnter',
            'handleMouseLeave',
            'handlePlayingEnd',
            'handleSelect',
            'handleScroll',
            'handleSourceScopeChange',
            'handleTagClick',
            'requestRemoteQuery',
            'setFilteredDataRef'
        ]);
        this.state = {
            playingItem: null,
            filterQuery: '',
            selectedTag: ALL_TAG.tag,
            sourceScope: SOURCE_SCOPE_PUBLIC,
            queryPageIndex: 1,
            loaded: false
        };
        this.remoteQueryPending = false;
        this.requestRemoteQueryDebounced = debounce(this.requestRemoteQuery, 250);
    }
    componentDidMount () {
        // Allow the spinner to display before loading the content
        setTimeout(() => {
            this.setState({loaded: true});
        });
        if (this.props.setStopHandler) this.props.setStopHandler(this.handlePlayingEnd);
        if (this.props.onRequestQuery) {
            this.requestRemoteQuery({
                append: false,
                pageIndex: 1
            });
        }
    }
    componentDidUpdate (prevProps, prevState) {
        if (prevState.filterQuery !== this.state.filterQuery ||
            prevState.sourceScope !== this.state.sourceScope ||
            prevState.selectedTag !== this.state.selectedTag) {
            this.scrollToTop();
        }
    }
    componentWillUnmount () {
        this.requestRemoteQueryDebounced.cancel();
    }
    handleSelect (id) {
        this.handleClose();
        this.props.onItemSelected(this.getFilteredData()
            .find(item => this.constructKey(item) === id));
    }
    handleClose () {
        this.props.onRequestClose();
    }
    handleTagClick (tag) {
        if (this.state.playingItem === null) {
            this.setState({
                filterQuery: '',
                selectedTag: tag.toLowerCase()
            }, () => {
                this.requestRemoteQueryDebounced.cancel();
                this.requestRemoteQuery({
                    append: false,
                    pageIndex: 1
                });
            });
        } else {
            this.props.onItemMouseLeave((this.getFilteredData()
                .find(item => this.constructKey(item) === this.state.playingItem)));
            this.setState({
                filterQuery: '',
                playingItem: null,
                selectedTag: tag.toLowerCase()
            }, () => {
                this.requestRemoteQueryDebounced.cancel();
                this.requestRemoteQuery({
                    append: false,
                    pageIndex: 1
                });
            });
        }
    }
    handleMouseEnter (id) {
        // don't restart if mouse over already playing item
        if (this.props.onItemMouseEnter && this.state.playingItem !== id) {
            this.props.onItemMouseEnter(this.getFilteredData()
                .find(item => this.constructKey(item) === id));
            this.setState({
                playingItem: id
            });
        }
    }
    handleMouseLeave (id) {
        if (this.props.onItemMouseLeave) {
            this.props.onItemMouseLeave(this.getFilteredData()
                .find(item => this.constructKey(item) === id));
            this.setState({
                playingItem: null
            });
        }
    }
    handlePlayingEnd () {
        if (this.state.playingItem !== null) {
            this.setState({
                playingItem: null
            });
        }
    }
    handleFilterChange (event) {
        if (this.state.playingItem === null) {
            this.setState({
                filterQuery: event.target.value,
                selectedTag: ALL_TAG.tag
            }, () => {
                this.requestRemoteQueryDebounced({
                    append: false,
                    pageIndex: 1
                });
            });
        } else {
            this.props.onItemMouseLeave(this.getFilteredData()
                .find(item => this.constructKey(item) === this.state.playingItem));
            this.setState({
                filterQuery: event.target.value,
                playingItem: null,
                selectedTag: ALL_TAG.tag
            }, () => {
                this.requestRemoteQueryDebounced({
                    append: false,
                    pageIndex: 1
                });
            });
        }
    }
    handleFilterClear () {
        this.setState({filterQuery: ''}, () => {
            this.requestRemoteQueryDebounced.cancel();
            this.requestRemoteQuery({
                append: false,
                pageIndex: 1
            });
        });
    }
    handleSourceScopeChange (event) {
        const sourceScope = event.currentTarget.getAttribute('data-source-scope');
        if (!sourceScope || sourceScope === this.state.sourceScope) return;
        if (this.state.playingItem !== null && this.props.onItemMouseLeave) {
            this.props.onItemMouseLeave(this.getFilteredData()
                .find(item => this.constructKey(item) === this.state.playingItem));
        }
        this.setState({
            playingItem: null,
            sourceScope
        }, () => {
            this.requestRemoteQueryDebounced.cancel();
            this.requestRemoteQuery({
                append: false,
                pageIndex: 1
            });
        });
    }
    handleScroll (event) {
        if (!this.props.onRequestQuery ||
            this.props.isLoading ||
            this.remoteQueryPending ||
            !this.props.hasMore) {
            return;
        }

        const {scrollTop, scrollHeight, clientHeight} = event.currentTarget;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        if (distanceFromBottom <= 240) {
            this.requestRemoteQuery({
                append: true,
                pageIndex: this.state.queryPageIndex + 1
            });
        }
    }
    requestRemoteQuery (query) {
        if (!this.props.onRequestQuery) return null;

        const nextQuery = {
            filterQuery: this.state.filterQuery,
            selectedTag: this.state.selectedTag,
            sourceScope: this.state.sourceScope,
            pageIndex: this.state.queryPageIndex,
            pageSize: this.props.pageSize,
            append: false,
            ...query
        };

        this.remoteQueryPending = true;
        this.setState({
            queryPageIndex: nextQuery.pageIndex
        });

        const request = this.props.onRequestQuery(nextQuery);
        if (request && typeof request.then === 'function') {
            return request.then(
                response => {
                    this.remoteQueryPending = false;
                    return response;
                },
                () => {
                    this.remoteQueryPending = false;
                    return null;
                }
            );
        }
        this.remoteQueryPending = false;
        return request;
    }
    getFilteredData () {
        if (this.props.onRequestQuery) {
            return this.props.data;
        }
        if (this.state.selectedTag === ALL_TAG.tag) {
            if (!this.state.filterQuery) return this.props.data;
            return this.props.data.filter(dataItem => (
                (dataItem.tags || [])
                    // Second argument to map sets `this`
                    .map(String.prototype.toLowerCase.call, String.prototype.toLowerCase)
                    .concat(dataItem.name ?
                        (typeof dataItem.name === 'string' ?
                        // Use the name if it is a string, else use formatMessage to get the translated name
                            dataItem.name : this.props.intl.formatMessage(dataItem.name.props)
                        ).toLowerCase() :
                        null)
                    .join('\n') // unlikely to partially match newlines
                    .indexOf(this.state.filterQuery.toLowerCase()) !== -1
            ));
        }
        return this.props.data.filter(dataItem => (
            dataItem.tags &&
            dataItem.tags
                .map(String.prototype.toLowerCase.call, String.prototype.toLowerCase)
                .indexOf(this.state.selectedTag) !== -1
        ));
    }
    constructKey (data) {
        return typeof data.name === 'string' ? data.name : data.rawURL;
    }
    scrollToTop () {
        if (this.filteredDataRef) {
            this.filteredDataRef.scrollTop = 0;
        }
    }
    setFilteredDataRef (ref) {
        this.filteredDataRef = ref;
    }
    renderElement (data) {
        const key = this.constructKey(data);
        return (<LibraryItem
            bluetoothRequired={data.bluetoothRequired}
            collaborator={data.collaborator}
            description={data.description}
            disabled={data.disabled}
            extensionId={data.extensionId}
            featured={data.featured}
            hidden={data.hidden}
            iconMd5={data.costumes ? data.costumes[0].md5ext : data.md5ext}
            iconRawURL={data.rawURL}
            icons={data.costumes}
            id={key}
            insetIconURL={data.insetIconURL}
            internetConnectionRequired={data.internetConnectionRequired}
            isPlaying={this.state.playingItem === key}
            key={key}
            name={data.name}
            showPlayButton={this.props.showPlayButton}
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
            onSelect={this.handleSelect}
        />);
    }
    renderData (data) {
        if (this.state.selectedTag !== ALL_TAG.tag || !this.props.withCategories) {
            return data.map(item => this.renderElement(item));
        }

        const dataByCategory = Object.groupBy(data, el => el.category);
        const categoriesOrder = Object.values(CATEGORIES);

        return Object.entries(dataByCategory)
            .sort(([key1], [key2]) => categoriesOrder.indexOf(key1) - categoriesOrder.indexOf(key2))
            .map(([key, values]) =>
                (<div
                    key={key}
                    className={styles.libraryCategory}
                >
                    {key === 'undefined' ?
                        null :
                        <span className={styles.libraryCategoryTitle}>
                            {this.props.intl.formatMessage(messages[key])}
                        </span>
                    }
                    <div
                        className={styles.libraryCategoryItems}
                    >
                        {values.map(item => this.renderElement(item))}
                    </div>
                </div>));
    }
    render () {
        const data = this.getFilteredData();
        const showInitialSpinner = !this.state.loaded ||
            (this.props.onRequestQuery && this.props.isLoading && data.length === 0);
        const showFilterBar = this.props.filterable || this.props.tags || this.props.scopeFilterable;
        return (
            <Modal
                fullScreen
                contentLabel={this.props.title}
                id={this.props.id}
                onRequestClose={this.handleClose}
            >
                {showFilterBar && (
                    <div className={styles.filterBar}>
                        {this.props.filterable && (
                            <Filter
                                className={classNames(
                                    styles.filterBarItem,
                                    styles.filter
                                )}
                                filterQuery={this.state.filterQuery}
                                inputClassName={styles.filterInput}
                                placeholderText={this.props.intl.formatMessage(messages.filterPlaceholder)}
                                onChange={this.handleFilterChange}
                                onClear={this.handleFilterClear}
                            />
                        )}
                        {this.props.filterable && (this.props.scopeFilterable || this.props.tags) && (
                            <Divider className={classNames(styles.filterBarItem, styles.divider)} />
                        )}
                        {this.props.scopeFilterable && (
                            <div className={classNames(styles.filterBarItem, styles.scopeToggle)}>
                                <button
                                    className={classNames(styles.scopeButton, {
                                        [styles.scopeButtonActive]: this.state.sourceScope === SOURCE_SCOPE_PUBLIC
                                    })}
                                    data-source-scope={SOURCE_SCOPE_PUBLIC}
                                    type="button"
                                    onClick={this.handleSourceScopeChange}
                                >
                                    {'公共'}
                                </button>
                                <button
                                    className={classNames(styles.scopeButton, {
                                        [styles.scopeButtonActive]: this.state.sourceScope === SOURCE_SCOPE_MINE
                                    })}
                                    data-source-scope={SOURCE_SCOPE_MINE}
                                    type="button"
                                    onClick={this.handleSourceScopeChange}
                                >
                                    {'我的'}
                                </button>
                            </div>
                        )}
                        {this.props.scopeFilterable && this.props.tags && (
                            <Divider className={classNames(styles.filterBarItem, styles.divider)} />
                        )}
                        {this.props.tags &&
                            <div className={styles.tagWrapper}>
                                {tagListPrefix.concat(this.props.tags).map((tagProps, id) => (
                                    <TagButton
                                        active={this.state.selectedTag === tagProps.tag.toLowerCase()}
                                        className={classNames(
                                            styles.filterBarItem,
                                            styles.tagButton,
                                            tagProps.className
                                        )}
                                        key={`tag-button-${id}`}
                                        onClick={this.handleTagClick}
                                        {...tagProps}
                                    />
                                ))}
                            </div>
                        }
                    </div>
                )}
                <div
                    className={classNames(styles.libraryScrollGrid, {
                        [styles.withFilterBar]: showFilterBar
                    })}
                    ref={this.setFilteredDataRef}
                    onScroll={this.handleScroll}
                >
                    {showInitialSpinner ? (
                        <div className={styles.spinnerWrapper}>
                            <Spinner
                                large
                                level="primary"
                            />
                        </div>
                    ) : (
                        <React.Fragment>
                            {this.renderData(data)}
                            {this.props.onRequestQuery && this.props.isLoading && (
                                <div className={styles.loadMoreWrapper}>
                                    <Spinner level="primary" />
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </Modal>
        );
    }
}

LibraryComponent.propTypes = {
    data: PropTypes.arrayOf(
        /* eslint-disable react/no-unused-prop-types, lines-around-comment */
        // An item in the library
        PropTypes.shape({
            // @todo remove md5/rawURL prop from library, refactor to use storage
            md5: PropTypes.string,
            name: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.node
            ]),
            rawURL: PropTypes.string
        })
        /* eslint-enable react/no-unused-prop-types, lines-around-comment */
    ),
    filterable: PropTypes.bool,
    hasMore: PropTypes.bool,
    withCategories: PropTypes.bool,
    id: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    isLoading: PropTypes.bool,
    onItemMouseEnter: PropTypes.func,
    onItemMouseLeave: PropTypes.func,
    onItemSelected: PropTypes.func,
    onRequestClose: PropTypes.func,
    onRequestQuery: PropTypes.func,
    pageSize: PropTypes.number,
    setStopHandler: PropTypes.func,
    showPlayButton: PropTypes.bool,
    scopeFilterable: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.shape(TagButton.propTypes)),
    title: PropTypes.string.isRequired
};

LibraryComponent.defaultProps = {
    data: [],
    filterable: true,
    hasMore: false,
    isLoading: false,
    pageSize: 30,
    scopeFilterable: false,
    showPlayButton: false
};

export default injectIntl(LibraryComponent);
